import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Receipt,
  Plus,
  Trash,
  ArrowClockwise,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Topbar from "@/components/layout/Topbar";
import MoneyInput from "@/components/MoneyInput";
import api from "@/lib/api";

const fmtMoney = (v, moneda = "ARS") =>
  new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: moneda,
    minimumFractionDigits: 2,
  }).format(Number(v || 0));

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function GastosEstructuraPage() {
  const [gastos, setGastos] = useState([]);
  const [cuentasEgreso, setCuentasEgreso] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const [filtroCuenta, setFiltroCuenta] = useState("");
  const [filtroMoneda, setFiltroMoneda] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const [openForm, setOpenForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [form, setForm] = useState({
    fecha: todayISO(),
    cuenta_codigo: "",
    medio_pago_id: "",
    monto: 0,
    moneda: "ARS",
    concepto: "",
    beneficiario: "",
    observaciones: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (filtroCuenta) params.cuenta_codigo = filtroCuenta;
      if (filtroMoneda) params.moneda = filtroMoneda;
      const [g, cu, m] = await Promise.all([
        api.get("/gastos-estructura", { params }),
        api.get("/gastos-estructura/cuentas-egreso"),
        api.get("/medios-pago/mios", { params: { scope: "ambos" } }),
      ]);
      setGastos(g.data || []);
      setCuentasEgreso(cu.data || []);
      setMediosPago(m.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los gastos");
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta, filtroCuenta, filtroMoneda]);

  useEffect(() => {
    load();
  }, [load]);

  const gastosFiltrados = useMemo(() => {
    if (!q.trim()) return gastos;
    const query = q.toLowerCase();
    return gastos.filter(
      (g) =>
        (g.concepto || "").toLowerCase().includes(query) ||
        (g.beneficiario || "").toLowerCase().includes(query) ||
        (g.cuenta_nombre || "").toLowerCase().includes(query) ||
        (g.cuenta_codigo || "").toLowerCase().includes(query),
    );
  }, [gastos, q]);

  const totalPorMoneda = useMemo(() => {
    const acc = { ARS: 0, USD: 0 };
    for (const g of gastosFiltrados) {
      acc[g.moneda || "ARS"] = (acc[g.moneda || "ARS"] || 0) + Number(g.monto || 0);
    }
    return acc;
  }, [gastosFiltrados]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cuenta_codigo) return toast.error("Seleccioná una cuenta contable de egreso");
    if (!form.medio_pago_id) return toast.error("Seleccioná un medio de pago");
    if (!form.concepto.trim()) return toast.error("Ingresá un concepto");
    const monto = parseFloat(form.monto || 0);
    if (!monto || monto <= 0) return toast.error("El monto debe ser mayor a 0");
    try {
      await api.post("/gastos-estructura", {
        fecha: form.fecha,
        cuenta_codigo: form.cuenta_codigo,
        medio_pago_id: form.medio_pago_id,
        monto,
        moneda: form.moneda,
        concepto: form.concepto.trim(),
        beneficiario: form.beneficiario.trim() || null,
        observaciones: form.observaciones.trim() || null,
      });
      toast.success("Gasto registrado");
      setOpenForm(false);
      setForm({
        fecha: todayISO(),
        cuenta_codigo: "",
        medio_pago_id: "",
        monto: 0,
        moneda: "ARS",
        concepto: "",
        beneficiario: "",
        observaciones: "",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el gasto");
    }
  };

  const eliminar = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/gastos-estructura/${confirmDel.id}`);
      toast.success("Gasto eliminado — saldo de caja revertido");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  return (
    <>
      <Topbar />
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-neutral-800 flex items-center gap-2">
              <Receipt size={28} weight="fill" className="text-red-800" />
              Gastos de estructura
            </h1>
            <div className="text-[12px] text-neutral-500 mt-0.5">
              Sueldos, viáticos, alquileres y otros egresos sin factura formal. Se
              imputan directo al plan de cuentas de egresos y descuentan la caja del
              medio de pago elegido.
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              onClick={load}
              className="h-9 gap-2"
              data-testid="gastos-refresh"
            >
              <ArrowClockwise
                size={15}
                weight="bold"
                className={loading ? "animate-spin" : ""}
              />
              Actualizar
            </Button>
            <Button
              onClick={() => setOpenForm(true)}
              className="h-9 gap-2 bg-[#921A0E] hover:bg-[#7d1509] text-white"
              data-testid="gastos-nuevo"
            >
              <Plus size={15} weight="bold" /> Nuevo gasto
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_150px_260px_140px] gap-2 items-center">
          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-500"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar concepto, cuenta o beneficiario…"
              className="pl-8 h-9"
              data-testid="gastos-search"
            />
          </div>
          <Input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="h-9"
            data-testid="gastos-fecha-desde"
          />
          <Input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="h-9"
            data-testid="gastos-fecha-hasta"
          />
          <select
            value={filtroCuenta}
            onChange={(e) => setFiltroCuenta(e.target.value)}
            className="h-9 rounded border border-neutral-300 bg-white px-2 text-sm"
            data-testid="gastos-filtro-cuenta"
          >
            <option value="">Todas las cuentas de egreso</option>
            {cuentasEgreso.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.codigo} · {c.nombre}
              </option>
            ))}
          </select>
          <select
            value={filtroMoneda}
            onChange={(e) => setFiltroMoneda(e.target.value)}
            className="h-9 rounded border border-neutral-300 bg-white px-2 text-sm"
            data-testid="gastos-filtro-moneda"
          >
            <option value="">ARS + USD</option>
            <option value="ARS">Sólo ARS</option>
            <option value="USD">Sólo USD</option>
          </select>
        </div>

        {/* Totales */}
        <div className="flex flex-wrap gap-3">
          <div className="border border-neutral-200 rounded-sm px-3 py-1.5 bg-neutral-50">
            <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">
              Total ARS
            </div>
            <div className="text-lg font-bold text-neutral-800 font-mono" data-testid="gastos-total-ars">
              {fmtMoney(totalPorMoneda.ARS, "ARS")}
            </div>
          </div>
          <div className="border border-neutral-200 rounded-sm px-3 py-1.5 bg-neutral-50">
            <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">
              Total USD
            </div>
            <div className="text-lg font-bold text-neutral-800 font-mono" data-testid="gastos-total-usd">
              {fmtMoney(totalPorMoneda.USD, "USD")}
            </div>
          </div>
          <div className="border border-neutral-200 rounded-sm px-3 py-1.5 bg-neutral-50">
            <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">
              Cantidad
            </div>
            <div className="text-lg font-bold text-neutral-800 font-mono" data-testid="gastos-count">
              {gastosFiltrados.length}
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-neutral-100 text-[11px] uppercase font-mono text-neutral-600 border-b">
            <div className="col-span-1">Fecha</div>
            <div className="col-span-3">Cuenta contable</div>
            <div className="col-span-3">Concepto</div>
            <div className="col-span-1">Beneficiario</div>
            <div className="col-span-2">Medio de pago</div>
            <div className="col-span-1 text-right">Monto</div>
            <div className="col-span-1"></div>
          </div>
          <div className="max-h-[62vh] overflow-y-auto">
            {gastosFiltrados.map((g) => (
              <div
                key={g.id}
                className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-neutral-100 items-center text-sm hover:bg-neutral-50"
                data-testid={`gastos-row-${g.id}`}
              >
                <div className="col-span-1 font-mono text-[12px]">{g.fecha}</div>
                <div className="col-span-3 truncate">
                  <Badge variant="outline" className="text-[10px] font-mono mr-1">
                    {g.cuenta_codigo}
                  </Badge>
                  <span className="text-[12px] text-neutral-700">{g.cuenta_nombre}</span>
                </div>
                <div className="col-span-3 truncate" title={g.concepto}>
                  {g.concepto}
                  {g.observaciones && (
                    <div className="text-[10px] text-neutral-500 truncate" title={g.observaciones}>
                      {g.observaciones}
                    </div>
                  )}
                </div>
                <div className="col-span-1 truncate text-neutral-600">
                  {g.beneficiario || "—"}
                </div>
                <div className="col-span-2 truncate text-[12px] text-neutral-600">
                  <div>{g.medio_pago_nombre}</div>
                  <div className="text-[10px] text-neutral-400">{g.caja_nombre}</div>
                </div>
                <div className="col-span-1 text-right font-mono font-bold text-red-700">
                  {fmtMoney(g.monto, g.moneda)}
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-neutral-400 hover:text-red-700"
                    onClick={() => setConfirmDel(g)}
                    title="Eliminar y revertir el saldo de caja"
                    data-testid={`gastos-del-${g.id}`}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
            {gastosFiltrados.length === 0 && !loading && (
              <div className="p-8 text-center text-neutral-400 text-sm">
                No hay gastos registrados con los filtros actuales.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Diálogo Nuevo gasto */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-lg" data-testid="gastos-form-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo gasto de estructura</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase font-mono text-neutral-600">Fecha</label>
                <Input
                  type="date"
                  value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className="h-9 mt-1"
                  data-testid="gastos-form-fecha"
                  required
                />
              </div>
              <div>
                <label className="text-[11px] uppercase font-mono text-neutral-600">Moneda</label>
                <select
                  value={form.moneda}
                  onChange={(e) => setForm({ ...form, moneda: e.target.value, medio_pago_id: "" })}
                  className="h-9 rounded border border-neutral-300 bg-white px-2 text-sm w-full mt-1"
                  data-testid="gastos-form-moneda"
                >
                  <option value="ARS">ARS</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase font-mono text-neutral-600">
                Cuenta contable de egreso
              </label>
              <select
                value={form.cuenta_codigo}
                onChange={(e) => setForm({ ...form, cuenta_codigo: e.target.value })}
                className="h-9 rounded border border-neutral-300 bg-white px-2 text-sm w-full mt-1"
                data-testid="gastos-form-cuenta"
                required
              >
                <option value="">Seleccionar…</option>
                {cuentasEgreso.map((c) => (
                  <option key={c.codigo} value={c.codigo}>
                    {c.codigo} · {c.nombre}
                  </option>
                ))}
              </select>
              {cuentasEgreso.length === 0 && (
                <div className="text-[10px] text-red-600 mt-1">
                  No hay cuentas de egreso imputables en el plan de cuentas.
                  Cargalas desde Contabilidad → Plan de cuentas.
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] uppercase font-mono text-neutral-600">
                Medio de pago (define la caja que descuenta)
              </label>
              <select
                value={form.medio_pago_id}
                onChange={(e) => setForm({ ...form, medio_pago_id: e.target.value })}
                className="h-9 rounded border border-neutral-300 bg-white px-2 text-sm w-full mt-1"
                data-testid="gastos-form-medio-pago"
                required
              >
                <option value="">Seleccionar…</option>
                {mediosPago.map((mp) => (
                  <option key={mp.id} value={mp.id}>
                    {mp.nombre} → {mp.caja_nombre || "sin caja"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase font-mono text-neutral-600">Monto</label>
              <MoneyInput
                value={form.monto}
                onChange={(e) => setForm({ ...form, monto: e.target.value })}
                className="h-9 mt-1 font-mono text-right"
                data-testid="gastos-form-monto"
                required
              />
            </div>
            <div>
              <label className="text-[11px] uppercase font-mono text-neutral-600">Concepto</label>
              <Input
                value={form.concepto}
                onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                placeholder="Ej: Sueldo abril · Viáticos taller · Alquiler local"
                className="h-9 mt-1"
                data-testid="gastos-form-concepto"
                required
              />
            </div>
            <div>
              <label className="text-[11px] uppercase font-mono text-neutral-600">
                Beneficiario (opcional)
              </label>
              <Input
                value={form.beneficiario}
                onChange={(e) => setForm({ ...form, beneficiario: e.target.value })}
                placeholder="Nombre del empleado, proveedor, etc."
                className="h-9 mt-1"
                data-testid="gastos-form-beneficiario"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase font-mono text-neutral-600">
                Observaciones (opcional)
              </label>
              <Textarea
                value={form.observaciones}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                className="mt-1"
                rows={2}
                data-testid="gastos-form-obs"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpenForm(false)}
                data-testid="gastos-form-cancel"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-[#921A0E] hover:bg-[#7d1509] text-white"
                data-testid="gastos-form-submit"
              >
                Registrar gasto
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminación */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="gastos-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDel && (
                <>
                  Se eliminará el gasto de{" "}
                  <strong>{fmtMoney(confirmDel.monto, confirmDel.moneda)}</strong> —{" "}
                  {confirmDel.concepto}.
                  <br />
                  <br />
                  El saldo de la caja <strong>{confirmDel.caja_nombre}</strong> será
                  revertido y se borrará el asiento contable asociado.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gastos-del-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={eliminar}
              className="bg-[#921A0E] hover:bg-[#7d1509] text-white"
              data-testid="gastos-del-confirm"
            >
              Eliminar y revertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
